import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
//services
import changePassword from 'services/forgotPassword/changePassword'
import sendForgotEmailService from 'services/forgotPassword/sendEmail'
import { setError } from 'ducks/user'

// Components
import ForgotForm from '../../components/Forms/Forgot'
import Logo from '../../components/UI/Logo'
import { P, H2 } from '../../components/UI/Text'
import Separator from 'views/components/UI/Separator/Index'
import ChangePasswordForm from 'views/components/Forms/changePassword'
import { useNotification } from 'lib/context/notification.context'
import { useTranslation } from 'react-i18next'
import Input from 'views/components/UI/TextInput'
import { Button } from 'views/components/UI/Buttons'
import { verifyActionCode } from 'services/auth/action-code'

const PASSWORD_RECOVERY_TECHNICAL_ERROR_MESSAGE =
  'No fue posible procesar la solicitud en este momento. Inténtalo nuevamente más tarde o comunícate con el administrador del sistema.'

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 30px;
  @media (max-width: 1080px) {
    width: 120%;
    text-align: center;
  }
`

const WithOutParams = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const ForgotFormContainer: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { getWarning, getSuccess, getError } = useNotification()
  const navigate = useNavigate()
  const paramValue = localStorage.getItem('i18nextLng')

  const [emailSended, setEmailSended] = useState<boolean>(false)
  const [recoveryEmail, setRecoveryEmail] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [isCodeValidated, setCodeValidated] = useState<boolean>(false)
  const [isValidatingCode, setValidatingCode] = useState<boolean>(false)
  const [changePasswordSuccess, setChangePasswordSuccess] = useState<boolean>(false)

  const handleSendEmailForgot = (values: any): void => {
    const email = values.email?.trim().toLowerCase()
    sendForgotEmailService(email, paramValue || 'es')
      .then(() => {
        setRecoveryEmail(email)
        setEmailSended(true)
        getSuccess(t('global.common.forgotPassword.notifications.sendSuccess'))
        dispatch(setError(false))
      })
      .catch((error) => {
        const status = error?.response?.status

        if (status === 400) {
          getError(t('global.notifications.emailNotExist'))
          return
        }

        if (status === 422) {
          getWarning(t('global.notifications.emailNotMain'))
          return
        }

        if (status === 421) {
          getError(t('global.notifications.userNotActive'))
          return
        }

        getError(PASSWORD_RECOVERY_TECHNICAL_ERROR_MESSAGE)
      })
  }

  const handleChangePassword = (pass: string): void => {
    changePassword({
      password: pass,
    })
      .then(() => {
        getSuccess(t('global.common.forgotPassword.notifications.success'))
        setChangePasswordSuccess(true)
        setTimeout(() => {
          navigate('/')
        }, 3000)
      })
      .catch(() => {
        getWarning(t('global.common.forgotPassword.notifications.error'))
      })
  }

  const handleValidateCode = async (): Promise<void> => {
    if (!recoveryEmail || !code.trim()) {
      getWarning('Introduce el código temporal recibido por correo.')
      return
    }

    try {
      setValidatingCode(true)
      await verifyActionCode(recoveryEmail, code.trim(), 'password_recovery')
      setCodeValidated(true)
    } catch (error: any) {
      getWarning(error?.response?.data?.message || 'El código es inválido o ha expirado.')
    } finally {
      setValidatingCode(false)
    }
  }

  return (
    <Container>
      <Logo width="80%" />
      <Separator height="1rem" />
      <WithOutParams>
        {!emailSended ? (
            <>
              <Separator height="2rem" />
              <H2 sx={{ width: 400, margin: '0px 0px 20px 0px', alignSelf: 'center' }}>
                {t('global.common.forgotPassword.title')}
              </H2>
              <P sx={{ width: '90%', textAlign: 'center', alignSelf: 'center' }}>
                {t('global.common.forgotPassword.description')}
              </P>
              <Separator height="2rem" />
              <ForgotForm onSubmit={(e) => handleSendEmailForgot(e)} />
              <Separator height="1rem" />
              <Button onClick={() => setEmailSended(true)} type="button">
                Ya tengo un código
              </Button>
            </>
        ) : !isCodeValidated ? (
          <>
            <Separator height="2rem" />
            <H2 sx={{ width: 400, margin: '0px 0px 20px 0px', alignSelf: 'center' }}>
              Valida el código temporal
            </H2>
            <P sx={{ width: '90%', textAlign: 'center', alignSelf: 'center' }}>
              {recoveryEmail
                ? `Introduce el código enviado a ${recoveryEmail}.`
                : 'Introduce el correo y el código recibido.'}
            </P>
            <Separator height="1rem" />
            {!recoveryEmail && (
              <Input
                value={recoveryEmail}
                onChange={(event) => setRecoveryEmail(event.target.value.trim().toLowerCase())}
                type="email"
                name="recovery-email"
                label="Correo electrónico"
              />
            )}
            {!recoveryEmail && <Separator height="1rem" />}
            <Input
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              type="text"
              name="recovery-code"
              label="Código temporal"
            />
            <Button disabled={isValidatingCode} onClick={handleValidateCode} type="button">
              {isValidatingCode ? 'Validando código...' : 'Validar código'}
            </Button>
          </>
        ) : (
          <>
            {!changePasswordSuccess ? (
              <ChangePasswordForm onSubmit={(e) => handleChangePassword(e.password)} />
            ) : (
              <>
                <Separator height="2rem" />
                <H2 sx={{ width: 400, margin: '0px 0px 20px 0px', alignSelf: 'center' }}>
                  {t('global.common.forgotPassword.changedPassword')}
                </H2>
                <P sx={{ width: '90%', textAlign: 'center', alignSelf: 'center' }}>
                  {t('global.common.forgotPassword.changedPasswordDescription')}
                </P>
              </>
            )}
          </>
        )}
      </WithOutParams>
    </Container>
  )
}

export default ForgotFormContainer
